import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useTransactionContext } from '../contexts/TransactionContext';
import { formatCurrency, formatDate, formatTime } from '../utils/formatters';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import './Transactions.css';

const Transactions = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { transactions } = useTransactionContext();

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar transacciones
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    // Filtrar por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.tipo === filterType);
    }

    // Filtrar por fecha
    if (filterDate !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(tx => {
        const txDate = new Date(tx.fecha);
        
        switch (filterDate) {
          case 'today':
            return txDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return txDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return txDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.estudianteNombre?.toLowerCase().includes(term) ||
        tx.vendedorNombre?.toLowerCase().includes(term) ||
        tx.id?.toLowerCase().includes(term)
      );
    }

    // Ordenar por fecha descendente
    return filtered.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [transactions, filterType, filterDate, searchTerm]);

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'pago':
        return '💳';
      case 'recarga':
        return '💰';
      default:
        return '📝';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'pago':
        return '#ef4444';
      case 'recarga':
        return '#10b981';
      default:
        return '#6366f1';
    }
  };

  const getTransactionType = (type) => {
    switch (type) {
      case 'pago':
        return 'Pago';
      case 'recarga':
        return 'Recarga';
      default:
        return 'Transacción';
    }
  };

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayTxs = transactions.filter(tx => new Date(tx.fecha) >= today);
    const pagos = todayTxs.filter(tx => tx.tipo === 'pago');
    const recargas = todayTxs.filter(tx => tx.tipo === 'recarga');

    return {
      total: todayTxs.length,
      pagos: pagos.length,
      recargas: recargas.length,
      montoPagos: pagos.reduce((sum, tx) => sum + Math.abs(tx.monto), 0),
      montoRecargas: recargas.reduce((sum, tx) => sum + tx.monto, 0)
    };
  }, [transactions]);

  return (
    <div className="transactions-container">
      {/* Header */}
      <header className="transactions-header">
        <div className="header-content">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <span className="back-icon">←</span>
            <span>Volver</span>
          </button>
          <div className="header-title">
            <div className="title-icon">📊</div>
            <div>
              <h1>Historial de Transacciones</h1>
              <p>Registro completo de movimientos</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-icon">🏪</span>
            <span>{user?.nombre}</span>
          </div>
        </div>
      </header>

      <div className="transactions-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <Card variant="gradient" padding="medium" className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Transacciones Hoy</div>
            </div>
          </Card>

          <Card variant="default" padding="medium" className="stat-card">
            <div className="stat-icon" style={{ background: '#ef4444' }}>💳</div>
            <div className="stat-info">
              <div className="stat-value">{stats.pagos}</div>
              <div className="stat-label">Pagos ({formatCurrency(stats.montoPagos)})</div>
            </div>
          </Card>

          <Card variant="default" padding="medium" className="stat-card">
            <div className="stat-icon" style={{ background: '#10b981' }}>💰</div>
            <div className="stat-info">
              <div className="stat-value">{stats.recargas}</div>
              <div className="stat-label">Recargas ({formatCurrency(stats.montoRecargas)})</div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card variant="default" padding="medium" className="filters-card">
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">🔍</span>
                Buscar
              </label>
              <input
                type="text"
                className="search-input"
                placeholder="Estudiante, vendedor o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">📋</span>
                Tipo
              </label>
              <select
                className="filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="pago">Pagos</option>
                <option value="recarga">Recargas</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">📅</span>
                Período
              </label>
              <select
                className="filter-select"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="today">Hoy</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
              </select>
            </div>
          </div>

          {filteredTransactions.length > 0 && (
            <div className="results-count">
              Mostrando <strong>{filteredTransactions.length}</strong> de {transactions.length} transacciones
            </div>
          )}
        </Card>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <Card variant="default" padding="large" className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No hay transacciones</h3>
            <p>
              {searchTerm || filterType !== 'all' || filterDate !== 'all'
                ? 'No se encontraron transacciones con los filtros aplicados'
                : 'Aún no se han realizado transacciones'}
            </p>
          </Card>
        ) : (
          <div className="transactions-list">
            {filteredTransactions.map((tx) => (
              <Card
                key={tx.id}
                variant="default"
                padding="medium"
                hoverable
                clickable
                onClick={() => handleViewDetails(tx)}
                className="transaction-card"
              >
                <div className="tx-main">
                  <div
                    className="tx-icon"
                    style={{ background: getTransactionColor(tx.tipo) }}
                  >
                    {getTransactionIcon(tx.tipo)}
                  </div>

                  <div className="tx-details">
                    <div className="tx-header-row">
                      <div className="tx-type-badge" style={{ borderColor: getTransactionColor(tx.tipo) }}>
                        {getTransactionType(tx.tipo)}
                      </div>
                      <div className="tx-id">#{tx.id?.slice(0, 8)}</div>
                    </div>

                    <div className="tx-info-grid">
                      <div className="tx-info-item">
                        <span className="info-icon">👤</span>
                        <div className="info-content">
                          <span className="info-label">Estudiante</span>
                          <span className="info-value">{tx.estudianteNombre}</span>
                        </div>
                      </div>

                      <div className="tx-info-item">
                        <span className="info-icon">🏪</span>
                        <div className="info-content">
                          <span className="info-label">Vendedor</span>
                          <span className="info-value">{tx.vendedorNombre}</span>
                        </div>
                      </div>
                    </div>

                    <div className="tx-footer">
                      <div className="tx-date">
                        <span className="date-icon">📅</span>
                        {formatDate(tx.fecha)} • {formatTime(tx.fecha)}
                      </div>
                      <div
                        className="tx-amount"
                        style={{ color: getTransactionColor(tx.tipo) }}
                      >
                        {tx.tipo === 'pago' ? '-' : '+'}
                        {formatCurrency(Math.abs(tx.monto))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Detalle de Transacción"
        size="medium"
      >
        {selectedTransaction && (
          <div className="transaction-detail">
            <div className="detail-header">
              <div
                className="detail-icon"
                style={{ background: getTransactionColor(selectedTransaction.tipo) }}
              >
                {getTransactionIcon(selectedTransaction.tipo)}
              </div>
              <div className="detail-header-info">
                <div className="detail-type-badge" style={{ borderColor: getTransactionColor(selectedTransaction.tipo) }}>
                  {getTransactionType(selectedTransaction.tipo)}
                </div>
                <div className="detail-id">ID: {selectedTransaction.id}</div>
              </div>
            </div>

            <div className="detail-amount" style={{ color: getTransactionColor(selectedTransaction.tipo) }}>
              {selectedTransaction.tipo === 'pago' ? '-' : '+'}
              {formatCurrency(Math.abs(selectedTransaction.monto))}
            </div>

            <div className="detail-sections">
              <div className="detail-section">
                <h4 className="section-title">
                  <span className="section-icon">👤</span>
                  Información del Estudiante
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Nombre</span>
                    <span className="detail-value">{selectedTransaction.estudianteNombre}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">ID</span>
                    <span className="detail-value">{selectedTransaction.estudianteId}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="section-icon">🏪</span>
                  Información del Vendedor
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Nombre</span>
                    <span className="detail-value">{selectedTransaction.vendedorNombre}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">ID</span>
                    <span className="detail-value">{selectedTransaction.vendedorId}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="section-icon">💰</span>
                  Resumen Financiero
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Saldo Anterior</span>
                    <span className="detail-value">{formatCurrency(selectedTransaction.saldoAnterior)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Monto</span>
                    <span className="detail-value" style={{ color: getTransactionColor(selectedTransaction.tipo) }}>
                      {selectedTransaction.tipo === 'pago' ? '-' : '+'}
                      {formatCurrency(Math.abs(selectedTransaction.monto))}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Saldo Nuevo</span>
                    <span className="detail-value highlight">{formatCurrency(selectedTransaction.saldoNuevo)}</span>
                  </div>
                </div>
              </div>

              {selectedTransaction.productos && selectedTransaction.productos.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">
                    <span className="section-icon">🛒</span>
                    Productos ({selectedTransaction.productos.length})
                  </h4>
                  <div className="products-list">
                    {selectedTransaction.productos.map((producto, index) => (
                      <div key={index} className="product-item">
                        <div className="product-info">
                          <span className="product-name">{producto.nombre}</span>
                          <span className="product-qty">x{producto.cantidad}</span>
                        </div>
                        <div className="product-price">
                          {formatCurrency(producto.precio * producto.cantidad)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="section-icon">📅</span>
                  Fecha y Hora
                </h4>
                <div className="detail-date">
                  {formatDate(selectedTransaction.fecha)} a las {formatTime(selectedTransaction.fecha)}
                </div>
              </div>
            </div>

            <div className="detail-actions">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDetailModal(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Transactions;
